import { Entity, RGBColor } from '@core/models';
import { z } from 'zod';
import { BostonRedSoxConfig } from './boston-red-sox.conf';
import { ChicagoCubsConfig } from './chicago-cubs.conf';
import { ChicagoCubsSpring } from './cubs-spring.conf';
import { DefaultConfig } from './default.conf';
import { HoustonAstrosConfig } from './houston-astros.conf';
import { MiamiMarlinsConfig } from './miami-marlins.conf';
import { PittsburghPiratesConfig } from './pittsburgh-pirates.conf';
import { WashingtonNationalsConfig } from './washington-nationals.conf';

const RGB_COLOR_PATTERN = /^\d{1,3} \d{1,3} \d{1,3}$/;

function isRGBColor(value: unknown): value is RGBColor {
  if (typeof value !== 'string') {
    return false;
  }

  if (!RGB_COLOR_PATTERN.test(value)) {
    return false;
  }

  const components = value.split(' ').map(Number);
  return components.every(component => component >= 0 && component <= 255);
}

const rgbColorSchema = z.custom<RGBColor>(
  value => isRGBColor(value),
  'Expected RGB color in "R G B" format (0-255)'
);

export const configurationSchema = z.object({
  clientName: z.string(),
  appName: z.string(),
  logo: z.string(),
  analytics: z.string(),
  favicon: z.string(),
  email: z.string().email(),
  phone: z.string(),
  paymentMethod: z.string(),
  theme: z.object({
    'default-color': rgbColorSchema,
    'main-color': rgbColorSchema,
    'secondary-color': rgbColorSchema,
    'tertiary-color': rgbColorSchema,
    'accent-color': rgbColorSchema,
    red: rgbColorSchema,
    success: rgbColorSchema,
    'success-off-white': rgbColorSchema,
  }),
  dvmData: z.object({
    venueId: z.string(),
    mapId: z.string(),
    miniMapId: z.string(),
  }),
  dvmStyles: z.object({
    'seat-available-none-fill': rgbColorSchema,
    'seat-available-none-stroke': rgbColorSchema,
    'seat-available-disabled-fill': rgbColorSchema,
    'seat-available-disabled-stroke': rgbColorSchema,
    'seat-unavailable-none': rgbColorSchema,
    'seat-available-pending': rgbColorSchema,
    'section-available-normal-fill': rgbColorSchema,
    'section-available-normal-stroke': rgbColorSchema,
    'section-available-normal-group1-fill': rgbColorSchema,
    'section-available-normal-group1-stroke': rgbColorSchema,
    'section-available-hover-fill': rgbColorSchema,
    'section-available-hover-stroke': rgbColorSchema,
    'section-selected-normal-fill': rgbColorSchema,
    'section-selected-normal-stroke': rgbColorSchema,
    'section-selected-hover-stroke': rgbColorSchema,
  }),
});

export const configurationData: Entity = {
  default: DefaultConfig,
  chicagocubs: ChicagoCubsConfig,
  redsox: BostonRedSoxConfig,
  miamimarlins: MiamiMarlinsConfig,
  houstonastros: HoustonAstrosConfig,
  pittsburghpirates: PittsburghPiratesConfig,
  chicagocubsspring: ChicagoCubsSpring,
  washingtonnationals: WashingtonNationalsConfig,
};
